import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress } from '@mui/material';

type Props = {
  open: boolean;
  variant: 'deactivated' | 'active';
  onSubmitNew: () => void;
  onPrimaryAction: () => void;
  isLoading?: boolean;
};

const CONFIG = {
  deactivated: {
    title: 'Reactivate String Translation',
    description: 'This English string* already exists, but the Spanish translation connected to it was previously deactivated, and is therefore not visible in the Translation Hub. Would you like to reactivate it?',
    showFootnote: true,
    primaryButtonText: 'REACTIVATE EXISTING STRING',
  },
  active: {
    title: 'This string already exists',
    description: 'This English string already has a translation visible in the Translation Hub. Would you like to go to it?',
    showFootnote: false,
    primaryButtonText: 'GO TO TRANSLATION',
  },
};

const StringExistsDialog: React.FC<Props> = ({ open, variant, onSubmitNew, onPrimaryAction, isLoading = false }) => {
  const currentConfig = CONFIG[variant];
  const showLoadingSpinner = variant === 'deactivated' && isLoading;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      slotProps={{
        backdrop: {
          onClick: (event) => event.stopPropagation(),
        },
        paper: {
          sx: {
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{currentConfig.title}</DialogTitle>

      <DialogContent>
        <Typography gutterBottom sx={{ mb: 3 }}>
          {currentConfig.description}
        </Typography>

        <Typography gutterBottom sx={{ mb: 3 }}>
          If not, you can submit a different English string for translation.
        </Typography>

        {currentConfig.showFootnote && (
          <Typography gutterBottom sx={{ fontStyle: 'italic' }}>
            *We currently use natural language keys to identify our English strings. For example &lt;I'm-outside-of-Texas&gt; and &lt;I-am-outside-of-Texas&gt; are two different strings code wise. Despite meaning the exact same thing, the character differences designate them as separate keys.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" onClick={onSubmitNew} disabled={isLoading}>
          SUBMIT NEW STRING
        </Button>
        <Button 
          variant="contained" 
          onClick={onPrimaryAction} 
          disabled={isLoading} 
          startIcon={showLoadingSpinner ? <CircularProgress size={16} /> : null}
        >
          {currentConfig.primaryButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StringExistsDialog;
